import React, { useEffect, useId, useRef, useState } from 'react';
import { Box, useTheme, type SxProps, type Theme } from '@mui/material';
import { keyframes } from '@emotion/react';
import {
  MASCOT_EYES,
  MASCOT_EYE_RX,
  MASCOT_EYE_RY,
  MASCOT_GRAY,
  MASCOT_PURPLE,
  MASCOT_STATIC_PATH,
  MASCOT_VIEWBOX,
} from './opsMascotGeometry';

/**
 * Mascote estático do Mestti OPS.
 * - `active`: roxo, olhos abertos.
 * - `inactive`: cinza, olhos em "X".
 * Micro-animações são disparadas incrementando `playKey`:
 * - `animation="tap"`: pulinho com squash & stretch + piscada (header).
 * - `animation="activate"`: acorda — cor cinza→roxo, olhos X→abertos, pulinho e anel de brilho.
 * Respeita `prefers-reduced-motion`.
 */

const tap = keyframes`
  0%   { transform: translateY(0) scale(1, 1); }
  18%  { transform: translateY(4%) scale(1.12, 0.86); }
  45%  { transform: translateY(-16%) scale(0.92, 1.1); }
  70%  { transform: translateY(0) scale(1.08, 0.92); }
  85%  { transform: translateY(-2%) scale(0.98, 1.02); }
  100% { transform: translateY(0) scale(1, 1); }
`;

const wake = keyframes`
  0%   { transform: translateY(0) scale(1, 1) rotate(0deg); }
  20%  { transform: translateY(5%) scale(1.14, 0.84) rotate(0deg); }
  46%  { transform: translateY(-18%) scale(0.9, 1.12) rotate(-6deg); }
  64%  { transform: translateY(-18%) scale(0.96, 1.04) rotate(5deg); }
  82%  { transform: translateY(0) scale(1.08, 0.93) rotate(0deg); }
  100% { transform: translateY(0) scale(1, 1) rotate(0deg); }
`;

const blink = keyframes`
  0%, 100% { transform: scaleY(1); }
  40%, 60% { transform: scaleY(0.1); }
`;

const eyesOpen = keyframes`
  0%   { transform: scale(0); }
  55%  { transform: scale(0); }
  80%  { transform: scale(1.25); }
  100% { transform: scale(1); }
`;

const xFade = keyframes`
  0%   { transform: scale(1) rotate(0deg); opacity: 1; }
  50%  { transform: scale(0) rotate(90deg); opacity: 0; }
  100% { transform: scale(0) rotate(90deg); opacity: 0; }
`;

const ring = keyframes`
  0%   { transform: scale(0.55); opacity: 0; }
  35%  { opacity: 0.45; }
  100% { transform: scale(1.35); opacity: 0; }
`;

export interface OpsMascotIconProps {
  size?: number;
  state?: 'active' | 'inactive';
  /** Incrementar para tocar a animação novamente. 0 = sem animação. */
  playKey?: number;
  animation?: 'tap' | 'activate';
  /** Cor do corpo quando ativo (padrão: roxo da marca). */
  color?: string;
  title?: string;
  /**
   * `outline`: só o contorno, sem olhos, em `currentColor` — mesmo padrão dos
   * ícones de linha do header. `filled` (padrão): corpo preenchido com olhos.
   */
  variant?: 'filled' | 'outline';
  /** Espessura do traço do `outline`, em px de tela. */
  strokeWidth?: number;
  className?: string;
  sx?: SxProps<Theme>;
}

/** Em tamanhos pequenos o traço do "X" engrossa para continuar legível. */
const XEye: React.FC<{ cx: number; cy: number; bold: boolean }> = ({ cx, cy, bold }) => {
  const arm = bold ? 52 : 44;
  return (
    <g stroke="black" strokeWidth={bold ? 58 : 36} strokeLinecap="round">
      <line x1={cx - arm} y1={cy - arm} x2={cx + arm} y2={cy + arm} />
      <line x1={cx + arm} y1={cy - arm} x2={cx - arm} y2={cy + arm} />
    </g>
  );
};

export const OpsMascotIcon: React.FC<OpsMascotIconProps> = ({
  size = 20,
  state = 'active',
  playKey = 0,
  animation = 'tap',
  color = MASCOT_PURPLE,
  title,
  variant = 'filled',
  strokeWidth = 1.1,
  className,
  sx,
}) => {
  const inactiveFill = MASCOT_GRAY[useTheme().palette.mode];
  const maskId = `ops-mascot-eyes-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const active = state === 'active';
  const playing = playKey > 0;
  const bodyAnim = playing ? (animation === 'activate' ? wake : tap) : null;
  const duration = animation === 'activate' ? 760 : 460;

  // Na ativação, o "X" some e os olhos abrem com um pequeno overshoot.
  const waking = playing && animation === 'activate' && active;

  // Piscada ocasional só no estado ativo e fora de animações (bem sutil, espaçada).
  const [idleBlink, setIdleBlink] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!active) return undefined;
    const schedule = () => {
      timer.current = window.setTimeout(() => {
        setIdleBlink((n) => n + 1);
        schedule();
      }, 4200 + Math.random() * 5200);
    };
    schedule();
    return () => window.clearTimeout(timer.current);
  }, [active]);

  const { x, y, size: vb } = MASCOT_VIEWBOX;
  const eyeAnimation = waking
    ? `${eyesOpen} ${duration}ms cubic-bezier(0.34,1.56,0.64,1) both`
    : playing && animation === 'tap'
      ? `${blink} 260ms ease-in-out 120ms both`
      : idleBlink > 0
        ? `${blink} 180ms ease-in-out both`
        : 'none';

  return (
    <Box
      component="span"
      className={className}
      data-brand="logo"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      sx={{
        position: 'relative',
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        lineHeight: 0,
        ...((sx as object) || {}),
      }}
    >
      {waking && (
        <Box
          key={`ring-${playKey}`}
          component="span"
          sx={{
            position: 'absolute',
            inset: '-25%',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color}55 0%, ${color}00 68%)`,
            animation: `${ring} ${duration}ms ease-out both`,
            pointerEvents: 'none',
            '@media (prefers-reduced-motion: reduce)': { display: 'none' },
          }}
        />
      )}
      <Box
        key={`body-${playKey}`}
        component="svg"
        viewBox={`${x} ${y} ${vb} ${vb}`}
        width={size}
        height={size}
        sx={{
          display: 'block',
          overflow: 'visible',
          transformOrigin: '50% 92%',
          animation: bodyAnim ? `${bodyAnim} ${duration}ms cubic-bezier(0.3,0.7,0.4,1) both` : 'none',
          '& .ops-eyes': {
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: active ? eyeAnimation : 'none',
          },
          '& .ops-x-eyes': {
            transformBox: 'fill-box',
            transformOrigin: 'center',
            animation: waking ? `${xFade} ${duration}ms ease-in both` : 'none',
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '& .ops-eyes, & .ops-x-eyes': { animation: 'none' },
            '& .ops-x-eyes': { opacity: waking ? 0 : 1 },
          },
        }}
      >
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse" x={x - 100} y={y - 100} width={vb + 200} height={vb + 200}>
            <rect x={x - 100} y={y - 100} width={vb + 200} height={vb + 200} fill="white" />
            {/* olhos abertos */}
            <g key={`eyes-${playKey}-${idleBlink}`} className="ops-eyes" opacity={active ? 1 : 0}>
              {MASCOT_EYES.map((e) => (
                <ellipse key={e.x} cx={e.x} cy={e.y} rx={MASCOT_EYE_RX} ry={MASCOT_EYE_RY} fill="black" />
              ))}
            </g>
            {/* olhos em X (desativado) — na ativação, giram e somem */}
            {(!active || waking) && (
              <g key={`x-${playKey}`} className="ops-x-eyes">
                {MASCOT_EYES.map((e) => (
                  <XEye key={e.x} cx={e.x} cy={e.y} bold={size < 48} />
                ))}
              </g>
            )}
          </mask>
        </defs>
        {variant === 'outline' ? (
          <path
            d={MASCOT_STATIC_PATH}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ) : (
          <path
            d={MASCOT_STATIC_PATH}
            mask={`url(#${maskId})`}
            style={{
              fill: active ? color : inactiveFill,
              transition: `fill ${waking ? 420 : 200}ms ease ${waking ? 120 : 0}ms`,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default OpsMascotIcon;
